import type { LibraryAssetItem } from '../../api/client'

/**
 * VistoeraTASK-00249 §26 — optimistic summary/category-card update using
 * each removed item's REAL `asset_type` (no hardcoded category deltas).
 * Shared by both single delete and batch delete in `ImageAssetCenterPage`
 * so the counter math is exactly one code path for both (mirrors the
 * shared server-side `delete_picker_item`/`batch_delete_picker_items`
 * lifecycle). Only adjusts keys that already exist in `prevSummary` — an
 * unknown/missing key is left alone rather than fabricated, and the
 * background `load()` reconciliation this always pairs with corrects any
 * residual drift. Never mutates `prevSummary` in place. `delta=-1` for an
 * optimistic removal, `delta=1` to roll back on a whole-request failure.
 */
export function applyDeleteDelta(
  prevSummary: Record<string, number>,
  removedItems: Pick<LibraryAssetItem, 'asset_type'>[],
  delta: 1 | -1,
): Record<string, number> {
  if (removedItems.length === 0) return prevSummary
  const next = { ...prevSummary }
  if (typeof next.all === 'number') next.all = Math.max(0, next.all + delta * removedItems.length)
  const perKeyDelta = new Map<string, number>()
  for (const item of removedItems) {
    const key = item.asset_type
    if (!key) continue
    perKeyDelta.set(key, (perKeyDelta.get(key) ?? 0) + delta)
  }
  for (const [key, d] of perKeyDelta) {
    if (typeof next[key] === 'number') next[key] = Math.max(0, next[key] + d)
  }
  return next
}
